/* eslint-disable import/no-cycle */

import { DataProvider } from "@shared/constants/common";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";

import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../../services/useBasketPaymentService";
import useInvoicePaymentService from "../../services/useInvoicePaymentService";
import { PurchaseOptsType } from "../../types";
import PaymentHandler from "../PaymentHandler";
import PaymentGatewayFactory from "../../PaymentGatewayFactory";
import ShowpassPaymentGateway from "../../gateways/ShowpassPaymentGateway/ShowpassPaymentGateway";
import { type PaymentManagerConfig } from "../../PaymentManager";
import PaymentGateway from "../../gateways/PaymentGateway";

/**
 * The default payment handler for free purchases.
 *
 * Since the payment is free, this should not implement any third-party payment gateway at all,
 * as we can avoid platform fees by keeping everything in-house.
 */
class FreePaymentHandler extends PaymentHandler {
  public static handlerName = "FreePaymentHandler";

  HAS_ASYNCHRONOUS_CONFIRMATION = false;
  HAS_ASYNCHRONOUS_CAPTURE = false;

  is_initialized = true;
  gateway: ShowpassPaymentGateway | null = null;

  constructor(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    servicesContext: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    super(config, basket, servicesContext);

    const selectedGateway = PaymentGatewayFactory.getOrCreateGateway(
      config,
      basket,
      servicesContext
    );

    if (!(selectedGateway instanceof ShowpassPaymentGateway)) {
      throw new PaymentConfigurationError(
        `Free payment handler only supports Showpass payment gateway for zero-cost transactions, but received ${selectedGateway.constructor.name}. Free payments should not use third-party gateways.`,
        {
          metadata: {
            supportedGateways: ["ShowpassPaymentGateway"],
            receivedGateway: selectedGateway.constructor.name,
            reason:
              "Free payments avoid platform fees by bypassing third-party processors",
          },
        }
      );
    }

    this.gateway = selectedGateway;
  }

  async initialize() {
    return this;
  }

  // eslint-disable-next-line class-methods-use-this
  async processCheckout(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Cannot process free payment because the internal payment gateway is not initialized",
        {
          metadata: {
            operation: "processCheckout",
          },
          userMessage: "Please try refreshing the page.",
        }
      );
    }

    if (this.gateway instanceof ShowpassPaymentGateway) {
      await this.gateway.confirmPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else {
      throw new PaymentProcessingError(
        "Cannot process free payment because the gateway is not the expected Showpass payment gateway",
        {
          metadata: {
            operation: "processCheckout",
            gatewayType:
              (this.gateway as PaymentGateway)?.constructor?.name || "unknown",
          },
          userMessage:
            "Your payment could not be processed. Please try a different payment method.",
        }
      );
    }

    return this;
  }

  async postProcessCheckout() {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Cannot complete free payment processing because the internal payment gateway is not available",
        {
          metadata: {
            operation: "postProcessCheckout",
          },
          userMessage:
            "Your payment could not be processed. Please try a different payment method.",
        }
      );
    }

    return this.gateway.postProcessCheckout();
  }
}

export default FreePaymentHandler;
